import {
  Component,
  OnInit,
  forwardRef,
  Output,
  EventEmitter,
  Input,
} from "@angular/core";
import { FilterCompleteService } from "src/app/filter-complete/filter-complete";
import { InvoiceLabelType } from "src/app/model/invoice-label-type";
import { TaxpayerType } from "../../../../model/taxpayer-type";
import { InvoicePoint } from "../../../../model/invoice-point";
import {
  FormBuilder,
  FormGroup,
  ControlValueAccessor,
  FormArray,
  AbstractControl,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { InvoicePointService } from "../../../../services/invoice-cash-desk/invoice-point.service";
import { BehaviorSubject } from "rxjs";
import { FilterService } from "../../../../services/filter.service";
import { InvoiceTax } from "../../../../model/invoice-tax";
import { InvoiceLabelService } from "../../../../services/invoice-label/invoice-label.service";
import { BillingInvoice } from "src/app/model/billing/billing-invoice";
import { BillingInvoicePeriod } from "../../../../model/billing/billing-invoice-period";
import { ClientSearch } from "../../../../model/client-search";
import { BillingInvoiceAssociateFilter } from "../../../../model/billing/billing-invoice-associate-filter";
import { ClientService } from "../../../../services/client.service";
import { toFixedTrunc } from "../../../../infrastructure/numeric-trunc";

export const BILLING_INVOICE_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BillingInvoiceEditControlComponent),
  multi: true,
};

@Component({
  selector: "app-billing-invoice-edit-control",
  providers: [BILLING_INVOICE_CONTROL_VALUE_ACCESSOR],
  templateUrl: "./billing-invoice-edit.component.html",
  styleUrls: ["./billing-invoice-edit.component.css"],
})
export class BillingInvoiceEditControlComponent
  implements OnInit, ControlValueAccessor
{
  loadingTaxes: boolean;
  form: FormGroup;
  onChange;
  displayInvoiceTaxesColumns = ["TaxTypeName", "Aliquot", "Amount"];
  invoiceTaxesDataSource = new BehaviorSubject<AbstractControl[]>([]);
  filterCompleteBillinInvoicePeriod: FilterCompleteService<BillingInvoicePeriod>;
  billingInvoicePeriods: BillingInvoicePeriod[] = [];
  taxpayerTypes: TaxpayerType[] = [];
  invoicePoints: InvoicePoint[] = [];
  invoiceLabelTypes: InvoiceLabelType[] = [];
  totalAmount: string;
  load: boolean;

  @Input() enabledClientSearch: boolean;
  @Output() invoicePointChange = new EventEmitter();
  @Output() clientChange = new EventEmitter();
  @Output() loaded = new EventEmitter();
  @Input() visibleCirculacionAbierta: boolean;
  @Input() visibleIsRejectedOptions: boolean;
  @Input() visibleDatesFromTo: boolean;
  @Input() visibleBillingInvoiceAssociate: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private filterService: FilterService,
    private clientService: ClientService,
    private invoiceLabelService: InvoiceLabelService,
    private invoicePointService: InvoicePointService
  ) {
    this.initInfo();
  }

  get filteredBillinInvoicePeriodOptions() {
    return this.filterCompleteBillinInvoicePeriod
      ? this.filterCompleteBillinInvoicePeriod.filteredOptions
      : [];
  }

  get invoiceTaxes() {
    return <FormArray>this.form.get("InvoiceTaxes");
  }

  get f() {
    return this.form.controls;
  }

  ngOnInit() {}

  private async loadControls() {
    if (this.invoicePoints.length === 0) {
      this.invoicePoints =
        await this.invoicePointService.getInvoicePointsBilling();
    }

    if (this.taxpayerTypes.length === 0) {
      this.taxpayerTypes = await this.filterService.getTaxpayerType();
    }

    if (this.billingInvoicePeriods.length === 0) {
      this.billingInvoicePeriods =
        this.filterService.getBillingInvoicePeriods();
      this.filterCompleteBillinInvoicePeriod =
        new FilterCompleteService<BillingInvoicePeriod>(
          this.billingInvoicePeriods,
          this.form.controls.BillingInvoicePeriod
        );
    }

    if (!this.enabledClientSearch) {
      this.form.controls["ClientSearch"].disable();
    }
  }

  async writeValue(obj: BillingInvoice) {
    if (obj) {
      await this.loadControls();

      this.form.patchValue({
        Amount: obj.Amount,
        InvoicePoint: !obj.InvoicePoint
          ? this.invoicePoints[0]
          : obj.InvoicePoint,
        InvoiceDate: obj.InvoiceDate,
        InvoiceFrom: obj.InvoiceFrom,
        InvoiceTo: obj.InvoiceTo,
        Description: obj.Description,
        ClientSearch: obj.ClientSearch,
        OriginalIIBBTaxAliquotId: obj.OriginalIIBBTaxAliquotId,
        InvoiceExpiredDate: obj.InvoiceExpiredDate,
        OriginalInvoiceNumberBookTypeId: obj.OriginalInvoiceNumberBookTypeId,
        OriginalAmount: obj.OriginalAmount,
        OriginalBillingInvoiceId: obj.OriginalBillingInvoiceId,
        OriginalBillingInvoiceManualId: obj.OriginalBillingInvoiceManualId,
        OriginalBillingInvoiceDate: obj.OriginalBillingInvoiceDate,
      });

      this.form.controls.InvoiceTaxes = this.formBuilder.array([]);

      if (this.taxpayerTypes && obj.TaxpayerType) {
        await this.setTaxpayerType(obj.TaxpayerType);
        if (obj.InvoiceLabelType) {
          this.setInvoiceLabelType(obj.InvoiceLabelType);
        }
      }

      if (obj.BillingInvoicePeriod) {
        const period = this.billingInvoicePeriods.find(
          (p) => p.Id === obj.BillingInvoicePeriod.Id
        );
        this.form.patchValue({ BillingInvoicePeriod: period });
      }

      let amountItems = 0;

      if (obj.InvoiceTaxes && obj.InvoiceTaxes.length > 0) {
        obj.InvoiceTaxes.forEach((item) => {
          this.addInvoiceTax(item);
          amountItems += item.Amount;
        });
      }

      this.totalAmount = `Monto total ${toFixedTrunc(
        obj.Amount + amountItems,
        2
      )}`;

      if (!this.load) {
        this.loaded.emit();
      }

      this.load = true;
    }
  }

  private setInvoiceLabelType(invoiceLabelType: InvoiceLabelType) {
    const iLabelType = this.invoiceLabelTypes.find(
      (t) => t.Id === invoiceLabelType.Id
    );
    this.form.patchValue({ InvoiceLabelType: iLabelType });
  }

  private async setTaxpayerType(taxpayerType: TaxpayerType) {
    const tpayerType = this.taxpayerTypes.find((t) => t.Id === taxpayerType.Id);
    this.form.patchValue({ TaxpayerType: tpayerType });
    await this.taxpayerTypeSelected(tpayerType);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  private initInfo() {
    this.form = this.formBuilder.group({
      InvoicePoint: [null],
      InvoiceDate: [null],
      InvoiceExpiredDate: [null],
      InvoiceFrom: [null],
      InvoiceTo: [null],
      IsRejected: [null],
      IsCirculacionAbierta: [true],
      Description: [""],
      BillingInvoicePeriod: [null],
      OriginalInvoiceNumberBookTypeId: [null],
      TaxpayerType: [{ value: null, disabled: true }],
      InvoiceLabelType: [null],
      Amount: [{ value: "", disabled: true }],
      InvoiceTaxes: this.formBuilder.array([]),
      ClientSearch: [null],
      OriginalIIBBTaxAliquotId: [null],
      OriginalAmount: [null],
      OriginalBillingInvoiceId: [null],
      OriginalBillingInvoiceManualId: [null],
      OriginalBillingInvoiceDate: [null],
      OriginalCompanyId: [null],
      BillinInvoicePeriod: [null],
    });

    this.filterCompleteBillinInvoicePeriod =
      new FilterCompleteService<BillingInvoicePeriod>(
        this.billingInvoicePeriods,
        this.form.controls.BillingInvoicePeriod
      );

    this.form.valueChanges.subscribe(() => {
      this.changeBillingInvoice();
    });
  }

  async onInvoiceAssociateChange(value: BillingInvoiceAssociateFilter) {
    console.log(value);

    if (value) {
      console.log(value);

      this.form.patchValue({
        OriginalBillingInvoiceId: value.BillingInvoiceId,
        OriginalBillingInvoiceManualId: value.BillingInvoiceManualId,
        OriginalInvoiceNumberBookTypeId: value.InvoiceNumberBookTypeId,
        OriginalCompanyId: value.CompanyId,
        OriginalAmount: value.Amount,
      });

      if (value.ClientId && value.CompanyId) {
        const client = await this.clientService.getClientSearch(value.ClientId);
        this.form.patchValue({ ClientSearch: client });
        this.setValuesByClientChange(client);
        this.setInvoicePointIfExits(value.CompanyId, value.TaxNumber);
      }
    }
  }

  async setValuesByClientChange(value: ClientSearch) {
    if (value) {
      const tpayerType = this.taxpayerTypes.find(
        (t) => t.Id === value.TaxpayerTypeId
      );
      await this.setTaxpayerType(tpayerType);
      this.clientChange.emit(value);
    }
  }

  private setInvoicePointIfExits(companyId: number, taxNumber: string) {
    const invoicePoint = this.invoicePoints.find(
      (i) => i.CompanyId === companyId && i.TaxNumber === taxNumber
    );
    this.form.patchValue({ InvoicePoint: invoicePoint });
    this.changeInvoicePoint();
  }

  private addInvoiceTax(invoiceTax: InvoiceTax): void {
    const row = this.formBuilder.group({
      Id: [invoiceTax.Id],
      Aliquots: [invoiceTax.Aliquots],
      AliquotId: [invoiceTax.AliquotId],
      Aliquot: [invoiceTax.Aliquot],
      Amount: [invoiceTax.Amount],
      AmountBase: [invoiceTax.AmountBase],
      TaxTypeId: [invoiceTax.TaxTypeId],
      TaxTypeName: [invoiceTax.TaxTypeName],
      InvoiceId: [invoiceTax.InvoiceId],
    });

    this.invoiceTaxes.push(row);
    this.invoiceTaxesDataSource.next(this.invoiceTaxes.controls);
    this.changeBillingInvoice();
  }

  async taxpayerTypeSelected(taxpayerType: TaxpayerType) {
    this.form.patchValue({ InvoiceLabelType: null });
    if (taxpayerType) {
      const invoiceLabelTypeTaxInfo =
        await this.invoiceLabelService.getInvoiceLabelTypeAndTax(
          taxpayerType.Id
        );
      this.invoiceLabelTypes = invoiceLabelTypeTaxInfo.InvoiceLabelTypes;
      this.form.patchValue({
        InvoiceLabelType: invoiceLabelTypeTaxInfo.InvoiceLabelTypes[0],
      });
    }
    this.changeBillingInvoice();
  }

  displayAmountAliquots(index: number): string {
    return this.invoiceTaxes.controls[index].value.Aliquots;
  }

  displayAmount(index: number): number {
    return this.invoiceTaxes.controls[index].value.Amount;
  }

  displayTaxTypeName(index: number): string {
    return this.invoiceTaxes.controls[index].value.TaxTypeName;
  }

  changeBillingInvoice(): void {
    const billingInvoice = this.getBillingInvoice();
    if (this.onChange) {
      this.onChange(billingInvoice);
    }
  }

  getBillingInvoice(): BillingInvoice {
    if (this.form) {
      return <BillingInvoice>this.form.getRawValue();
    } else {
      return null;
    }
  }

  changeInvoicePoint() {
    this.invoicePointChange.emit();
  }
}
